'use client'

import { Link, useParams } from 'react-router-dom'
import { useState } from 'react'
import {
  Star,
  ShoppingCart,
  Heart,
  Share2,
  Truck,
  Shield,
  Package,
  ChevronLeft,
  ChevronRight,
  Bell,
  Check,
  Minus,
  Plus,
} from 'lucide-react'
import { useProductBySlug, useProducts } from '@/hooks/useProducts'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Card } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Skeleton } from '@/components/ui/skeleton'
import { useCartStore } from '@/stores/cart'
import { useWishlistStore } from '@/stores/wishlist'
import { useCurrencyStore } from '@/stores/currency'
import ECommerceHeader from '@/components/layout/ECommerceHeader'
import Footer from '@/components/layout/Footer'
import ProductCard from '@/components/product/ProductCard'
import ProductReviews from '@/components/product/ProductReviews'

export default function ProductDetailsPage() {
  const { slug } = useParams<{ slug: string }>()
  const { data: product, isLoading, error } = useProductBySlug(slug!)

  const [selectedImage, setSelectedImage] = useState(0)
  const [quantity, setQuantity] = useState(1)
  const [addedToCart, setAddedToCart] = useState(false)
  const [showNotifyModal, setShowNotifyModal] = useState(false)
  const [notifyEmail, setNotifyEmail] = useState('')
  const [notifySubmitted, setNotifySubmitted] = useState(false)

  const addItem = useCartStore((state) => state.addItem)
  const { isInWishlist, toggleItem } = useWishlistStore()
  const getSymbol = useCurrencyStore((state) => state.getSymbol)
  const convert = useCurrencyStore((state) => state.convert)

  // Fetch related products
  const { data: relatedData } = useProducts(
    product ? { category: product.category_id, limit: 4 } : undefined,
    { enabled: !!product }
  )
  const relatedProducts = relatedData?.products?.filter((p) => p.id !== product?.id).slice(0, 4) || []

  const handleAddToCart = () => {
    if (!product) return
    addItem(
      {
        product_id: product.id,
        title: product.title,
        image: product.images[0]?.url || '',
        price: product.pricing.current_price,
        original_price: product.pricing.original_price,
        vendor_name: product.vendor.name,
      },
      quantity
    )
    setAddedToCart(true)
    setTimeout(() => setAddedToCart(false), 2000)
  }

  const handleToggleWishlist = () => {
    if (!product) return
    toggleItem(product.id)
  }

  const handleNotifySubmit = (e: React.FormEvent) => {
    e.preventDefault()
    setNotifySubmitted(true)
    setTimeout(() => {
      setShowNotifyModal(false)
      setNotifySubmitted(false)
      setNotifyEmail('')
    }, 2000)
  }

  const handleShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: product?.title,
          url: window.location.href,
        })
      } catch (err) {
        // User cancelled or error
      }
    } else {
      navigator.clipboard.writeText(window.location.href)
      alert('Link copied to clipboard!')
    }
  }

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background">
        <ECommerceHeader />
        <main className="container mx-auto px-4 py-8">
          <div className="grid md:grid-cols-2 gap-8">
            <Skeleton className="aspect-square rounded-lg" />
            <div className="space-y-4">
              <Skeleton className="h-8 w-3/4" />
              <Skeleton className="h-6 w-1/2" />
              <Skeleton className="h-12 w-1/3" />
              <Skeleton className="h-24 w-full" />
              <Skeleton className="h-12 w-full" />
            </div>
          </div>
        </main>
        <Footer />
      </div>
    )
  }

  if (error || !product) {
    return (
      <div className="min-h-screen bg-background">
        <ECommerceHeader />
        <main className="container mx-auto px-4 py-16 text-center">
          <h1 className="text-2xl font-bold text-foreground mb-4">Product Not Found</h1>
          <p className="text-muted-foreground mb-8">
            The product you're looking for doesn't exist or has been removed.
          </p>
          <Link to="/">
            <Button variant="primary">Back to Home</Button>
          </Link>
        </main>
        <Footer />
      </div>
    )
  }

  const inWishlist = isInWishlist(product.id)

  return (
    <div className="min-h-screen bg-background">
      <ECommerceHeader />

      <main className="container mx-auto px-4 py-8">
        {/* Breadcrumb */}
        <nav className="flex items-center gap-2 text-sm text-muted-foreground mb-6">
          <Link to="/" className="hover:text-primary">
            Home
          </Link>
          <span>/</span>
          <Link to={`/?category=${product.category_id}`} className="hover:text-primary">
            {product.category_name}
          </Link>
          <span>/</span>
          <span className="text-foreground">{product.title}</span>
        </nav>

        <div className="grid lg:grid-cols-2 gap-8 mb-12">
          {/* Image Gallery */}
          <div className="space-y-4">
            <div className="relative aspect-square overflow-hidden rounded-lg bg-muted">
              <img
                src={product.images[selectedImage]?.url || product.images[0]?.url}
                alt={product.title}
                className="w-full h-full object-cover"
              />
              {product.pricing.discount_percentage > 0 && (
                <div className="absolute top-4 left-4">
                  <Badge variant="default" className="bg-accent text-accent-foreground text-sm px-3 py-1">
                    -{product.pricing.discount_percentage}% OFF
                  </Badge>
                </div>
              )}
              {/* Image Navigation */}
              {product.images.length > 1 && (
                <>
                  <button
                    onClick={() => setSelectedImage((prev) => (prev === 0 ? product.images.length - 1 : prev - 1))}
                    className="absolute left-2 top-1/2 -translate-y-1/2 p-2 rounded-full bg-card/80 hover:bg-card shadow-md"
                  >
                    <ChevronLeft className="h-5 w-5" />
                  </button>
                  <button
                    onClick={() => setSelectedImage((prev) => (prev === product.images.length - 1 ? 0 : prev + 1))}
                    className="absolute right-2 top-1/2 -translate-y-1/2 p-2 rounded-full bg-card/80 hover:bg-card shadow-md"
                  >
                    <ChevronRight className="h-5 w-5" />
                  </button>
                </>
              )}
            </div>

            {/* Thumbnails */}
            {product.images.length > 1 && (
              <div className="flex gap-2 overflow-x-auto pb-2">
                {product.images.map((image, idx) => (
                  <button
                    key={idx}
                    onClick={() => setSelectedImage(idx)}
                    className={`flex-shrink-0 w-20 h-20 rounded-md overflow-hidden border-2 transition-colors ${
                      selectedImage === idx ? 'border-primary' : 'border-border'
                    }`}
                  >
                    <img src={image.url} alt={image.alt} className="w-full h-full object-cover" />
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Product Details */}
          <div className="space-y-6">
            {/* Title & Badges */}
            <div>
              <div className="flex items-center gap-2 mb-2">
                <Badge variant="outline" className="text-xs">
                  {product.vendor.name}
                </Badge>
                {product.vendor.is_verified && (
                  <Badge variant="default" className="text-xs bg-primary">
                    Verified Seller
                  </Badge>
                )}
                {product.badges.includes('choice') && (
                  <Badge variant="default" className="text-xs bg-brand-orange">
                    BotTola Choice
                  </Badge>
                )}
              </div>
              <h1 className="text-2xl md:text-3xl font-bold text-foreground">{product.title}</h1>
            </div>

            {/* Rating */}
            <div className="flex items-center gap-3">
              <div className="flex items-center gap-1">
                {Array.from({ length: 5 }).map((_, i) => (
                  <Star
                    key={i}
                    className={`h-5 w-5 ${
                      i < Math.floor(product.rating.average) ? 'fill-current text-yellow-500' : 'text-gray-300'
                    }`}
                  />
                ))}
              </div>
              <span className="text-sm text-muted-foreground">
                {product.rating.average} ({product.rating.count} reviews)
              </span>
            </div>

            {/* Price */}
            <div className="space-y-1">
              <div className="flex items-baseline gap-3">
                <span className="text-3xl md:text-4xl font-bold text-brand-orange">
                  {getSymbol()}
                  {convert(product.pricing.current_price).toLocaleString()}
                </span>
                {product.pricing.discount_percentage > 0 && (
                  <span className="text-lg text-muted-foreground line-through">
                    {getSymbol()}
                    {convert(product.pricing.original_price).toLocaleString()}
                  </span>
                )}
              </div>
              <p className="text-sm text-muted-foreground">Tax included. Shipping calculated at checkout.</p>
            </div>

            {/* Stock Status */}
            <div className="flex items-center gap-2">
              <Package className="h-5 w-5 text-muted-foreground" />
              <span
                className={`text-sm font-medium ${
                  product.stock_status === 'in_stock'
                    ? 'text-success'
                    : product.stock_status === 'low_stock'
                      ? 'text-orange-500'
                      : 'text-destructive'
                }`}
              >
                {product.stock_status === 'in_stock'
                  ? `In Stock (${product.stock_quantity} available)`
                  : product.stock_status === 'low_stock'
                    ? `Only ${product.stock_quantity} left - Order soon!`
                    : 'Out of Stock'}
              </span>
            </div>

            {/* Quantity & Add to Cart */}
            {product.stock_status !== 'out_of_stock' ? (
              <div className="space-y-4">
                <div className="flex items-center gap-4">
                  <span className="text-sm font-medium">Quantity:</span>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setQuantity(Math.max(1, quantity - 1))}
                      disabled={quantity <= 1}
                    >
                      <Minus className="h-4 w-4" />
                    </Button>
                    <span className="w-12 text-center font-medium">{quantity}</span>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setQuantity(Math.min(product.stock_quantity, quantity + 1))}
                      disabled={quantity >= product.stock_quantity}
                    >
                      <Plus className="h-4 w-4" />
                    </Button>
                  </div>
                </div>

                <div className="flex gap-3">
                  <Button variant="primary" size="lg" className="flex-1" onClick={handleAddToCart}>
                    {addedToCart ? (
                      <>
                        <Check className="h-5 w-5 mr-2" />
                        Added to Cart
                      </>
                    ) : (
                      <>
                        <ShoppingCart className="h-5 w-5 mr-2" />
                        Add to Cart
                      </>
                    )}
                  </Button>
                  <Button variant="outline" size="lg" onClick={handleToggleWishlist}>
                    <Heart className={`h-5 w-5 ${inWishlist ? 'fill-current text-destructive' : ''}`} />
                  </Button>
                  <Button variant="outline" size="lg" onClick={handleShare}>
                    <Share2 className="h-5 w-5" />
                  </Button>
                </div>
              </div>
            ) : (
              <div className="space-y-4">
                <Button
                  variant="outline"
                  size="lg"
                  className="w-full"
                  onClick={() => setShowNotifyModal(true)}
                >
                  <Bell className="h-5 w-5 mr-2" />
                  Notify Me When Available
                </Button>
                <div className="flex gap-3">
                  <Button variant="outline" size="lg" className="flex-1" onClick={handleToggleWishlist}>
                    <Heart className={`h-5 w-5 mr-2 ${inWishlist ? 'fill-current text-destructive' : ''}`} />
                    {inWishlist ? 'In Wishlist' : 'Add to Wishlist'}
                  </Button>
                  <Button variant="outline" size="lg" onClick={handleShare}>
                    <Share2 className="h-5 w-5" />
                  </Button>
                </div>
              </div>
            )}

            {/* Shipping & Delivery Info */}
            <Card className="p-4 space-y-3">
              <div className="flex items-center gap-3">
                <Truck className="h-5 w-5 text-primary" />
                <div>
                  <p className="text-sm font-medium">
                    {product.shipping.free_shipping
                      ? 'Free Shipping'
                      : `Shipping: ${getSymbol()}${convert(product.shipping.cost)}`}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    Estimated delivery: {product.shipping.estimated_days} business days
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <Shield className="h-5 w-5 text-primary" />
                <div>
                  <p className="text-sm font-medium">Secure Payment</p>
                  <p className="text-xs text-muted-foreground">SSL encrypted checkout</p>
                </div>
              </div>
            </Card>

            {/* Description */}
            <div>
              <h3 className="text-lg font-semibold mb-2">Description</h3>
              <p className="text-sm text-muted-foreground leading-relaxed">{product.description}</p>
            </div>

            {/* Key Features */}
            {product.key_features && product.key_features.length > 0 && (
              <div>
                <h3 className="text-lg font-semibold mb-2">Key Features</h3>
                <ul className="list-disc list-inside space-y-1 text-sm text-muted-foreground">
                  {product.key_features.map((feature, idx) => (
                    <li key={idx}>{feature}</li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </div>

        {/* Related Products */}
        {relatedProducts.length > 0 && (
          <section className="mt-12">
            <h2 className="text-2xl font-bold text-foreground mb-6">Related Products</h2>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {relatedProducts.map((relatedProduct) => (
                <ProductCard key={relatedProduct.id} product={relatedProduct} showBuyNow={false} />
              ))}
            </div>
          </section>
        )}

        {/* Customer Reviews */}
        <section className="mt-12">
          <ProductReviews productId={product.id} productTitle={product.title} />
        </section>
      </main>

      <Footer />

      {/* Notify Me Modal */}
      {showNotifyModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <Card className="w-full max-w-md mx-4 p-6">
            <h3 className="text-lg font-semibold mb-4">Notify Me When Available</h3>
            {notifySubmitted ? (
              <div className="text-center py-4">
                <Check className="h-12 w-12 text-success mx-auto mb-2" />
                <p className="text-sm text-muted-foreground">
                  We'll notify you when this product is back in stock!
                </p>
              </div>
            ) : (
              <form onSubmit={handleNotifySubmit}>
                <p className="text-sm text-muted-foreground mb-4">
                  Enter your email address and we'll let you know when "{product.title}" is back in stock.
                </p>
                <Input
                  type="email"
                  placeholder="Enter your email"
                  value={notifyEmail}
                  onChange={(e) => setNotifyEmail(e.target.value)}
                  required
                  className="mb-4"
                />
                <div className="flex gap-3">
                  <Button type="button" variant="outline" className="flex-1" onClick={() => setShowNotifyModal(false)}>
                    Cancel
                  </Button>
                  <Button type="submit" variant="primary" className="flex-1">
                    Notify Me
                  </Button>
                </div>
              </form>
            )}
          </Card>
        </div>
      )}
    </div>
  )
}
